export interface SearchApiListResponse<T> {
  data: T[];
  hasMore: boolean;
}

export interface SearchApiItemResponse<T> {
  data: T;
}

export type SearchApiScheduleTypeKey = 'scheduled' | 'arrivalOrder' | 'flex' | 'laboratorial';

export enum SearchApiScheduleType {
  SCHEDULED = 'SCHEDULED',
  ARRIVAL_ORDER = 'ARRIVAL_ORDER',
  FLEX = 'FLEX',
  LABORATORIAL = 'LABORATORIAL',
}

export type SearchApiOrderBy = 'DISTANCE' | 'PRICE' | 'RATING';

export type SearchApiDayTime = 'MORNING' | 'AFTERNOON';

export type SearchApiProfessionalGender = 'MALE' | 'FEMALE';

export enum SearchApiServiceChannel {
  IN_PERSON = 'IN_PERSON',
  TELEHEALTH = 'TELEHEALTH',
}

export enum SearchApiRatingCommentsOrderBy {
  NEWEST = 'NEWEST',
  OLDEST = 'OLDEST',
  HIGHEST_RATING = 'HIGHEST_RATING',
  LOWEST_RATING = 'LOWEST_RATING',
}

export interface SearchApiWorkingPeriod {
  id: string;
  start: string;
  end: string;
  weekday: number;
  duration: number;
}

export interface SearchApiSchedule {
  id: string;
  type: string;
  waitingTime?: number | null;
  workingPeriods?: SearchApiWorkingPeriod[] | null;
}

export interface SearchApiScheduleGroup {
  professionalId?: string | null;
  partnerId?: string | null;
  partnerFacilityId?: string | null;
  schedulesByScheduleType: Record<SearchApiScheduleTypeKey, SearchApiSchedule[]>;
  procedures: { id: string; name: string; slug: string }[];
  distance?: number | null;
  score: number;
  timeZone: string;
  timeOffset: number;
}

export interface SearchApiProcedurePromotion {
  id: string;
  originalPrice: number;
  price: number;
}

export interface SearchApiGroupProcedure {
  id: string;
  name: string;
  slug: string;
  price?: number | null;
  promotion?: SearchApiProcedurePromotion | null;
}

export interface SearchApiProcedureGroup {
  professionalId?: string | null;
  partnerId?: string | null;
  partnerFacilityId?: string | null;
  schedulesByScheduleType: Record<SearchApiScheduleTypeKey, SearchApiSchedule[]>;
  procedure: SearchApiGroupProcedure;
  timeZone: string;
  timeOffset: number;
}

export interface SearchApiPartnerProcedureOption {
  professionalId?: string | null;
  partnerId?: string | null;
  partnerFacilityId?: string | null;
  price?: number | null;
  promotion?: SearchApiProcedurePromotion | null;
  schedulesByScheduleType: Record<SearchApiScheduleTypeKey, SearchApiSchedule[]>;
  timeZone: string;
  timeOffset: number;
}

export interface SearchApiPartnerProcedureGroup {
  procedure: { id: string; name: string; slug: string };
  options?: SearchApiPartnerProcedureOption[];
}

export enum SearchApiProcedureType {
  LABORATORY = 'LABORATORY',
  NON_LABORATORY = 'NON_LABORATORY',
}

export interface SearchApiProcedure {
  id: string;
  name: string;
  code: string;
  tussCode: string;
  slug: string;
  type: string;
  category?: string | null;
  description?: string;
  availableForTelehealth: boolean;
  specialties?: { id: string; name: string; slug: string | null }[];
}

export interface SearchApiProfessionalLicense {
  certificationBodyId: string;
  certificationBodyInitials: string;
  certificationBodyName: string;
  licenseState: string;
  licenseRecord: string;
}

export interface SearchApiProfessionalSpecialty {
  id: string;
  name: string;
  slug: string | null;
  isActive?: boolean;
  isDisplayed?: boolean;
  specialistQualificationRecord?: string | null;
}

export interface SearchApiProfessionalFormation {
  type?: string;
  description?: string;
  location?: string;
  completionYear?: string | null;
}

export interface SearchApiProfessional {
  id: string;
  name: string;
  honorific?: string;
  gender?: string | null;
  slug?: string;
  avatar?: string;
  avatarLarge?: string;
  description?: string | null;
  specialties?: SearchApiProfessionalSpecialty[];
  licenses?: SearchApiProfessionalLicense[];
  professionalFormations?: SearchApiProfessionalFormation[];
  treatments?: string[];
  languageAndAccessibility?: string[];
  academicInfo?: string[];
  experiences?: string[];
  serviceChannels?: string[];
  followUp?: number | null;
  minAge?: number | null;
  maxAge?: number | null;
}

export interface SearchApiPartner {
  id: string;
  socialName: string;
  legalName: string;
  slug?: string | null;
  avatar?: string | null;
  avatarLarge?: string | null;
  description?: string | null;
  type?: string;
  specialties?: { id: string; name: string; slug: string | null }[];
  availableServiceChannels?: string[];
  facilities?: { id: string; slug?: string; type?: string }[];
}

export interface SearchApiFacilityContacts {
  email?: string;
  phones?: string[];
  whatsapp?: string;
}

export interface SearchApiFacilityStructure {
  airConditioning?: boolean | null;
  elevator?: boolean | null;
  parking?: boolean | null;
  specialAccess?: boolean | null;
}

export interface SearchApiFacilityImage {
  id?: string | null;
  name?: string | null;
  src?: string | null;
}

export interface SearchApiFacilityPartner {
  id: string;
  socialName?: string | null;
  slug?: string | null;
  avatar?: string | null;
  type?: string | null;
}

export interface SearchApiFacility {
  id: string;
  name?: string | null;
  slug?: string | null;
  type?: string;
  description?: string | null;
  city?: string | null;
  state?: string | null;
  ibgeCode?: string | null;
  neighborhood?: string | null;
  zipcode?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  number?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  contacts?: SearchApiFacilityContacts;
  structure?: SearchApiFacilityStructure | null;
  images?: SearchApiFacilityImage[] | null;
  partner?: SearchApiFacilityPartner | null;
  partnerId?: string;
}

export interface SearchApiFacilityExamPromotion {
  id: string;
  originalPrice: number;
  price: number;
}

export interface SearchApiFacilityExam {
  id: string;
  name: string;
  code?: string;
  tussCode?: string;
  price: number;
  promotion?: SearchApiFacilityExamPromotion | null;
}

export interface SearchApiBranch {
  id: string;
  name: string;
  slug?: string | null;
  avatar?: string | null;
  city?: string | null;
  state?: string | null;
  neighborhood?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  number?: string | null;
  zipcode?: string | null;
  whatsapps?: string[] | null;
  latitude?: number | null;
  longitude?: number | null;
  distance?: number | null;
}

export interface SearchApiRatingTiers {
  one: number;
  two: number;
  three: number;
  four: number;
  five: number;
}

export interface SearchApiRating {
  average: number;
  ratingsCount: number;
  commentsCount: number;
  tiers: SearchApiRatingTiers;
}

export interface SearchApiRatingCommentResponse {
  id: string;
  comment: string;
  createdAt: string;
}

export interface SearchApiRatingComment {
  id: string;
  authorName: string;
  comment: string;
  createdAt: string;
  ratingValue: number;
  responseComment?: SearchApiRatingCommentResponse | null;
}

export interface SearchApiProcedurePricing {
  partnerFacilityId: string;
  procedures: {
    procedureId: string;
    price: number;
    promotion: { id: string; originalPrice: number; price: number } | null;
  }[];
}

export interface SearchApiScheduleSlot {
  id: string;
  date: string;
  start: string;
  end: string;
  status: string;
  type: string;
}

export interface SearchApiScheduleSlotGroup {
  scheduleId: string;
  slots: SearchApiScheduleSlot[];
}
